import React from "react";
import { Box, Button, Paper, Popover, TextField, Typography } from "@mui/material";
import { Bookmark as BookmarkIcon } from "@mui/icons-material";
import RemarkSuggestions from "./RemarkSuggestions";
import type { RemarkSuggestion } from "../../../hooks/useRemarkSuggestions";

interface RemarkPopoverProps {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  suggestions: RemarkSuggestion[];
  suggestionsLoading: boolean;
  suggestionsError: string | null;
  onSelectSuggestion: (suggestion: RemarkSuggestion) => void;
  customRemark: string;
  onCustomRemarkChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  onBlur: () => void;
  /** Opens the saved-remarks dialog. */
  onBrowseAll: () => void;
  disabled: boolean;
}

/** The item row's remark editor: suggestions, a custom remark, and "Browse All". */
const RemarkPopover: React.FC<RemarkPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  suggestions,
  suggestionsLoading,
  suggestionsError,
  onSelectSuggestion,
  customRemark,
  onCustomRemarkChange,
  onPaste,
  onBlur,
  onBrowseAll,
  disabled,
}) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{
      vertical: 'center',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'center',
      horizontal: 'left',
    }}
  >
    <Paper sx={{ p: 2, width: { xs: 'calc(100vw - 32px)', sm: 500 }, maxWidth: 500, maxHeight: '80vh', overflow: 'auto' }}>
      {/* Auto-Suggestions */}
      <RemarkSuggestions
        suggestions={suggestions}
        loading={suggestionsLoading}
        error={suggestionsError}
        onSelectSuggestion={onSelectSuggestion}
        compact={false}
      />

      <Typography variant="subtitle2" sx={{ mb: 1, mt: (suggestions?.length ?? 0) > 0 ? 2 : 0 }}>
        {(suggestions?.length ?? 0) > 0 ? 'Or Enter Custom Remark' : 'Custom Remark'}
      </Typography>
      <TextField
        fullWidth
        size="small"
        value={customRemark}
        onChange={onCustomRemarkChange}
        onPaste={onPaste}
        onBlur={onBlur}
        disabled={disabled}
        placeholder="Enter custom remark (paste text with prices to auto-extract if price not validated)"
        multiline
        minRows={2}
        maxRows={8}
        sx={{
          mb: 2,
          '& .MuiInputBase-input': {
            resize: 'none', // Disable manual resize since we're auto-resizing
            overflow: 'hidden', // Hide scrollbar during auto-resize
          }
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<BookmarkIcon />}
          onClick={onBrowseAll}
          sx={{ bgcolor: 'background.paper' }}
        >
          Browse All
        </Button>
        <Button size="small" onClick={onClose}>Cancel</Button>
      </Box>
    </Paper>
  </Popover>
);

export default RemarkPopover;
